import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<void>;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await onLogin(username, password);
      navigate('/');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError('Too many login attempts. Please wait a minute and try again.');
      } else {
        setError('Invalid user/password combination');
      }
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.frame}>
        {/* Main Menu - matches original logged-out nav */}
        <div style={styles.menuBar}>
          <h1 style={styles.companyName}>Ming's Wealth Management Inc.</h1>
          <ul style={styles.menuList}>
            <li style={styles.menuItem}>Log In</li>
          </ul>
        </div>

        {/* Banner image */}
        <div style={styles.banner}>
          <img src="/header.jpg" alt="Toronto skyline" style={styles.bannerImg} />
        </div>

        {/* Login form area */}
        <div style={styles.viewport}>
          <div style={styles.loginBox}>
            <form onSubmit={handleSubmit}>
              <fieldset style={styles.fieldset}>
                <legend style={styles.legend}>Please Login</legend>
                {error && (
                  <div style={styles.flashBang}>{error}</div>
                )}
                <ol style={styles.fieldList}>
                  <li style={styles.field}>
                    <label style={styles.label} htmlFor="username">Username:</label>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      style={styles.input}
                      required
                    />
                  </li>
                  <li style={styles.field}>
                    <label style={styles.label} htmlFor="password">Password:</label>
                    <div style={{ flex: 1, display: 'flex', minWidth: 0 }}>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ ...styles.input, flex: 1 }}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={styles.toggleBtn}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </li>
                </ol>
              </fieldset>
              <fieldset style={styles.controls}>
                <input type="submit" value="Login" style={styles.button} />
              </fieldset>
            </form>
          </div>

          <p style={styles.caveat}>
            "Warning! All use of this system is monitored.
            Any person making unauthorized use of this system will be prosecuted."
          </p>
          <p style={styles.needCookies}>Note: Browser cookies must be enabled to login.</p>
        </div>

        <div style={{ clear: 'both' }}></div>
        <div style={styles.boilerplate}>
          <span>@2016 Ming's Wealth Management Inc.</span>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    backgroundColor: '#b5b2b5',
    minHeight: '100vh',
    fontFamily: 'Verdana, Arial, Helvetica, sans-serif',
  },
  frame: {
    backgroundColor: '#fff',
    border: '1px solid #1a1a1a',
    padding: '3px',
  },
  menuBar: {
    backgroundColor: '#cce6ff',
    borderBottom: '1px solid #727272',
    color: '#1a1a1a',
    fontSize: '0.8em',
    fontWeight: 'bold',
    height: '2.8em',
    lineHeight: '2.8em',
    position: 'relative',
  },
  companyName: {
    float: 'right',
    fontSize: '1.2em',
    color: '#7D78D7',
    verticalAlign: 'baseline',
    marginRight: '1em',
  },
  menuList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  menuItem: {
    display: 'inline',
    padding: '0.8em 1.2em',
    borderRight: '1px solid #fff',
    textTransform: 'uppercase',
    color: '#7D78D7',
  },
  banner: {
    width: '100%',
    overflow: 'hidden',
  },
  bannerImg: {
    width: '100%',
    display: 'block',
  },
  viewport: {
    border: '1px solid #e7e7e7',
  },
  loginBox: {
    margin: 'auto',
    paddingTop: '2.5em',
    width: '100%',
    maxWidth: '28em',
    boxSizing: 'border-box' as const,
    padding: '2.5em 1em 0',
  },
  fieldset: {
    border: '1px solid #808080',
    backgroundColor: '#fff',
    padding: '0.8em 1em 0',
    marginTop: '1em',
    boxSizing: 'border-box' as const,
  },
  legend: {
    fontWeight: 'bold',
    marginLeft: '0.5em',
    padding: '0 0.2em',
  },
  flashBang: {
    border: '1px dotted #700',
    backgroundColor: '#fff',
    color: '#a00',
    margin: '0.4em',
    padding: '1em 2.5em',
  },
  fieldList: {
    listStyle: 'none',
    padding: '0.8em 1em 0',
    margin: 0,
  },
  field: {
    paddingBottom: '1em',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    lineHeight: '1.3em',
    marginRight: '0.5em',
    textAlign: 'right' as const,
    flexShrink: 0,
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '2px 4px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '1em',
  },
  controls: {
    clear: 'left' as const,
    paddingTop: '0.3em',
    width: 'auto',
    textAlign: 'right' as const,
    border: 'none',
  },
  button: {
    padding: '4px 16px',
    backgroundColor: '#ccdcfc',
    color: '#000',
    border: '1px solid #808080',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    fontSize: '1em',
  },
  toggleBtn: {
    marginLeft: '4px',
    padding: '2px 8px',
    backgroundColor: '#eee',
    border: '1px solid #808080',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    fontSize: '0.85em',
    flexShrink: 0,
  },
  caveat: {
    fontWeight: 'bold',
    lineHeight: '1.3em',
    margin: 'auto',
    padding: '3em 1em 8em',
    textAlign: 'justify' as const,
    maxWidth: '26em',
  },
  needCookies: {
    fontSize: '0.8em',
    margin: 'auto',
    paddingBottom: '1em',
    textAlign: 'center' as const,
    maxWidth: '26em',
  },
  boilerplate: {
    marginTop: '1em',
    color: '#1a1a1a',
    fontSize: '0.7em',
  },
};

export default LoginPage;
